import type { FilterRuleModel } from '@/shared/generated'

import React from 'react'
import { InfoIcon } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'

import {
	PricingCampaignProvider,
	usePricingCampaignContext,
} from '@/features/pricing-campaign/edit'

import { usePricingCampaignQuery } from '@/entities/pricing-campaign'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { FormProvider } from '@/shared/components/HookForm'
import NavigationPrompt from '@/shared/components/NavigationPrompt'
import { Alert, AlertTitle } from '@/shared/components/ui/alert'
import { Blanket } from '@/shared/components/ui/blanket'
import { confirm } from '@/shared/components/ui/confirmer'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import { Modal, ModalContent } from '@/shared/components/ui/modal'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Spinner } from '@/shared/components/ui/spinner'
import { useMutationObservers } from '@/shared/lib/useMutationObservers'

import { PricingCampaignFormBody } from './PricingCampaignFormBody'
import { PricingCampaignFormFooter } from './PricingCampaignFormFooter'
import {
	PricingCampaignFormHeader,
	PricingCampaignFormInformation,
} from './PricingCampaignFormHeader'

type PricingCampaignModalProps = {
	globalPcSetId?: string
	isReadOnly?: boolean
	isOpen: boolean
	close: () => void
	pricingCampaignId?: string
	optimizationGroupId?: string | null
	scenarioId?: string | null
	draft?: {
		name?: string
		engine?: string
		assignment_filters?: FilterRuleModel[]
	}
}
export default function PricingCampaignModalWrapped({
	isOpen,
	close,
	draft,
	pricingCampaignId,
	isReadOnly,
	...props
}: PricingCampaignModalProps) {
	useMutationObservers({
		mutationKey: ['pricing-campaign-delete'],
		onPending: () => close(),
	})
	useMutationObservers({
		mutationKey: ['pricing-campaign-create'],
		onSuccess: () => close(),
	})
	useMutationObservers({
		mutationKey: ['pricing-campaign-update'],
		onSuccess: () => close(),
	})
	const pricingCampaignQuery = usePricingCampaignQuery(pricingCampaignId!, {
		enabled: !!pricingCampaignId,
	})

	const isLoading = pricingCampaignQuery.isLoading
	const pricingCampaign = pricingCampaignQuery.data
	const optimizationGroupId =
		pricingCampaign?.optimization_group_id || props.optimizationGroupId
	const scenarioId = pricingCampaign?.scenario_id || props.scenarioId
	const globalSetId = pricingCampaign?.global_pc_set_id || props.globalPcSetId

	return (
		<PricingCampaignProvider
			isReadOnly={isReadOnly}
			pricingCampaign={pricingCampaign}
			optimizationGroupId={optimizationGroupId}
			scenarioId={scenarioId}
			globalSetId={globalSetId}
			draft={draft}
		>
			<PricingCampaignModal isOpen={isOpen} close={close}>
				{isLoading ? <PricingCampaignModalSkeleton /> : <PricingCampaignForm />}
			</PricingCampaignModal>
		</PricingCampaignProvider>
	)
}
const PricingCampaignModal = ({
	isOpen,
	close,
	children,
}: {
	isOpen: boolean
	close: () => void
	children: React.ReactNode
}) => {
	const { t } = useTranslation()

	const context = usePricingCampaignContext()
	const handleOpenChange = async (open: boolean) => {
		if (context.methods.formState.isDirty && !open) {
			const answer = await confirm({
				title: t('navigation.blocked.pc_settings.title'),
				description: <Trans i18nKey='navigation.blocked.pc_settings.message' />,
				actionText: t('navigation.blocked.pc_settings.ok'),
				cancelText: t('navigation.blocked.pc_settings.cancel'),
			})
			if (answer) {
				close()
			}
		} else if (!open) {
			close()
		}
	}

	return (
		<Modal open={isOpen} onOpenChange={handleOpenChange}>
			<ModalContent
				data-testid='pc-modal'
				className='h-[calc(100vh-2rem)] max-w-5xl p-0'
			>
				<ErrorBoundary>{children}</ErrorBoundary>
			</ModalContent>
		</Modal>
	)
}
const PricingCampaignModalSkeleton = () => (
	<div className='flex size-full flex-col overflow-hidden rounded-lg'>
		<div className='h-16 w-full bg-secondary p-4' />
		<div className='flex w-full flex-col gap-2 border-b p-4 pb-2'>
			<Skeleton className='h-10 w-96' />
			<div className='flex items-center gap-5'>
				<Skeleton className='h-11 w-52' />
				<Skeleton className='h-11 w-52' />
				<Skeleton className='h-11 w-52' />
			</div>
			<div className='flex items-center gap-5 pt-4'>
				<Skeleton className='h-5 w-24' />
				<Skeleton className='h-5 w-24' />
				<Skeleton className='h-5 w-24' />
			</div>
		</div>
		<div className='size-full bg-secondary p-4'>
			<Skeleton className='h-96 w-full' />
		</div>
		<div className='mt-auto h-16 w-full bg-accent p-4' />
	</div>
)
function PricingCampaignForm() {
	const { t } = useTranslation()

	const context = usePricingCampaignContext()
	if (context.isLoading) {
		return <PricingCampaignModalSkeleton />
	}

	return (
		<>
			<NavigationPrompt
				when={context.methods.formState?.isDirty}
				title={t('navigation.blocked.pc_settings.title')}
				message={<Trans i18nKey='navigation.blocked.pc_settings.message' />}
				okText={t('navigation.blocked.pc_settings.ok')}
				cancelText={t('navigation.blocked.pc_settings.cancel')}
			/>
			<FormProvider
				methods={context!.methods}
				onSubmit={context.handleSubmit}
				className='flex h-full flex-col overflow-hidden p-4'
			>
				<Blanket
					className='absolute flex items-center justify-center rounded-lg bg-white/50 backdrop-blur-lg dark:bg-black/50'
					isTinted={context.isPending}
				>
					<Empty>
						<EmptyHeader>
							<EmptyMedia variant='icon'>
								<Spinner strokeWidth={6} className='text-primary' />
							</EmptyMedia>
							<EmptyDescription>
								{t('pricing_campaign_loading_hint')}
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				</Blanket>
				<PricingCampaignFormHeader />
				<PricingCampaignFormInformation />
				{context.isScenarioContext && (
					<Alert variant='info' className='mb-4'>
						<InfoIcon />
						<AlertTitle>
							{t('pc.scenario_pricing_campaign_hint', {
								defaultValue:
									'This is scenario pricing campign and will not nave effect to the master data',
							})}
						</AlertTitle>
					</Alert>
				)}
				{!context.isReadyToChange && (
					<Alert variant='warning' className='mb-4'>
						<InfoIcon />
						<AlertTitle>
							{t('pc.optimization.update_is_forbidden', {
								defaultValue:
									'Optimization status don`t allow to make any chages, please wait or revert optimization manually.',
							})}
						</AlertTitle>
					</Alert>
				)}
				{context.isReadOnly && (
					<Alert variant='warning' className='mb-4'>
						<InfoIcon />
						<AlertTitle>
							{t('pc.optimization.update_is_forbidden.is_read_only', {
								defaultValue:
									'You are not allowed to edit this pricing campaign here because it is a main scenario pricing campaign.',
							})}
						</AlertTitle>
					</Alert>
				)}
				<PricingCampaignFormBody />
				<PricingCampaignFormFooter />
			</FormProvider>
		</>
	)
}
